import React, { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { fmtPKR, fmtDate } from "@/lib/format";
import { verifyRound } from "./verify";

const RESULT = { active: "text-yellow-400", cashed_out: "text-emerald-400", lost: "text-red-400", refunded: "text-blue-400" };

export const MyBets = ({ bets }) => (
  bets.length === 0 ? <p className="text-gray-500 text-sm py-8 text-center">No entries yet.</p> : (
    <div className="divide-y divide-white/5 max-h-96 overflow-y-auto">
      {bets.map((b) => (
        <div key={b.id} className="flex items-center justify-between py-3 text-sm" data-testid={`aviator-bet-${b.id}`}>
          <div><p className="text-white">Round #{b.round_no} · {fmtPKR(b.amount)}</p><p className="text-xs text-gray-500">{fmtDate(b.created_at)}</p></div>
          <span className={`font-semibold ${RESULT[b.status]}`}>
            {b.status === "cashed_out" ? `${b.cashout_multiplier.toFixed(2)}x · +${fmtPKR(b.payout)}` : b.status === "lost" ? `-${fmtPKR(b.amount)}` : b.status}
          </span>
        </div>
      ))}
    </div>
  )
);

const VerifyRow = ({ r }) => {
  const [res, setRes] = useState(null);
  const run = async () => {
    const v = await verifyRound(r.server_seed, r.round_no, r.max_multiplier);
    setRes(v.sha === r.commit_hash && Math.abs(v.crash - r.crash_point) < 0.001 ? "ok" : "fail");
  };
  return (
    <div className="py-3 text-xs" data-testid={`aviator-round-${r.round_no}`}>
      <div className="flex items-center justify-between">
        <span className="text-white text-sm">#{r.round_no} · <span className={r.crash_point >= 2 ? "text-emerald-400" : "text-red-400"}>{r.crash_point.toFixed(2)}x</span></span>
        <button onClick={run} className="text-orange-400 hover:text-orange-300 flex items-center gap-1" data-testid={`aviator-verify-${r.round_no}`}>
          <ShieldCheck className="w-4 h-4" />{res === "ok" ? "Verified" : res === "fail" ? "Mismatch" : "Verify"}
        </button>
      </div>
      <p className="text-gray-600 truncate keep-ltr mt-1">Seed: {r.server_seed}</p>
      <p className="text-gray-600 truncate keep-ltr">Hash: {r.commit_hash}</p>
    </div>
  );
};

export const AviatorTabs = ({ refreshKey }) => {
  const [bets, setBets] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [rules, setRules] = useState([]);
  useEffect(() => {
    api.get("/aviator/my-bets").then(({ data }) => setBets(data)).catch(() => {});
    api.get("/aviator/rounds").then(({ data }) => setRounds(data)).catch(() => {});
  }, [refreshKey]);
  useEffect(() => { api.get("/aviator/rules").then(({ data }) => setRules(data.rules)).catch(() => {}); }, []);
  return (
    <Tabs defaultValue="mine" className="ec-card p-6">
      <TabsList className="bg-white/5">
        <TabsTrigger value="mine" data-testid="aviator-tab-mine">My History</TabsTrigger>
        <TabsTrigger value="rounds" data-testid="aviator-tab-rounds">Rounds & Verify</TabsTrigger>
        <TabsTrigger value="rules" data-testid="aviator-tab-rules">Rules</TabsTrigger>
      </TabsList>
      <TabsContent value="mine"><MyBets bets={bets} /></TabsContent>
      <TabsContent value="rounds">
        {rounds.length === 0 ? <p className="text-gray-500 text-sm py-8 text-center">No completed rounds yet.</p> :
          <div className="divide-y divide-white/5 max-h-96 overflow-y-auto">{rounds.map((r) => <VerifyRow key={r.id} r={r} />)}</div>}
      </TabsContent>
      <TabsContent value="rules">
        <ol className="list-decimal ps-5 space-y-2 text-sm text-gray-300" data-testid="aviator-rules">{rules.map((x, i) => <li key={i}>{x}</li>)}</ol>
      </TabsContent>
    </Tabs>
  );
};
